import type { PushSdkConfig, PushSdkConsentState } from './push-sdk-config.ts'
import type { ECommItemWithRating, ECommItemWithoutQuantity, ECommItemWithQuantity } from './ecomm.ts'
import type { SubscriberStatus } from '../enums/subscriber-status.ts'

/** @public */
export type Callback<T = void> = (() => Promise<T>) | (() => T)

/** @public */
export type PushSdkEventCallback = (...args: any[]) => void | Promise<void>

/** @public */
export type GetNotificationsPausedStateCallback = (isPaused: boolean) => any

/** @public */
export type GetPromptingSuppressedStateCallback = (isSuppressed: boolean) => any

/** @public */
export type PushSdkUser = {
    puuid: string
    externalId: string | null
    subscriberState: SubscriberStatus.SUBSCRIBED | SubscriberStatus.DENIED | SubscriberStatus.DISMISSED | 'none'
}

/** @public */
export type PushSdkContext = {
    domainKey: string
    user: PushSdkUser
}

/** @public */
export type PushSdkPromptEventPayload = {
    prompt: {
        id: number
    }
    subscribed: boolean
}

/** @public */
export type PushSdkPermissionEventPayload = {
    prompt: {
        id: number
    }
}

/** @public */
export enum PushSdkCommand {
    LOAD = 'load',
    SHOW_PROMPT = 'show_prompt',
    PROFILE = 'profile',
    PROFILE_APPEND = 'profile_append',
    PROFILE_REMOVE = 'profile_remove',
    PAGE_TAG_VISIT = 'page_tag_visit',
    EXTERNAL_ID = 'external_id',
    DEREGISTER_EXTERNAL_ID = 'deregister_external_id',
    REQUEST_USER_DELETION = 'request_user_deletion',
    OPT_OUT = 'opt_out',
    OPT_IN = 'opt_in',
    CONSENT = 'consent',
    VIEW_ITEM = 'view_item',
    SAVE_ITEM = 'save_item',
    UNSAVE_ITEM = 'unsave_item',
    COMPLETE_ITEM = 'complete_item',
    UNCOMPLETE_ITEM = 'uncomplete_item',
    RATE_ITEM = 'rate_item',
    UNRATE_ITEM = 'unrate_item',
    ADD_TO_CART = 'add_to_cart',
    PURCHASE = 'purchase',
    UPDATE_CART = 'update_cart',
    DEBUG = 'debug',
    CONFIRM_CONSENT = 'confirm_consent',
    PAUSE_PUSH_NOTIFICATIONS = 'pause_notifications',
    RESUME_PUSH_NOTIFICATIONS = 'resume_notifications',
    GET_PUSH_NOTIFICATIONS_PAUSED_STATE = 'get_notifications_paused_state',
    SUPPRESS_PROMPTS = 'suppress_prompts',
    ALLOW_PROMPTS = 'allow_prompts',
    GET_PROMPTING_SUPPRESSED_STATE = 'get_prompting_suppressed_state',
}

/** @public */
export enum PushSdkEvent {
    WEB_PUSH_SUPPORTED = 'web_push_supported',
    WEB_PUSH_UNSUPPORTED = 'web_push_unsupported',

    LOADED = 'loaded',
    READY = 'ready',

    PROMPT_ELIGIBLE = 'prompt_eligible',
    PROMPT_INELIGIBLE = 'prompt_ineligible',
    PROMPT_SHOWN = 'prompt_shown',
    PROMPT_DISMISSED = 'prompt_dismissed',
    PROMPT_ALLOWED = 'prompt_allowed',

    PERMISSION_SHOWN = 'permission_shown',
    PERMISSION_ALLOWED = 'permission_allowed',
    PERMISSION_DENIED = 'permission_denied',
    PERMISSION_DISMISSED = 'permission_dismissed',
    LEGACY_PERMISSION_SHOWN = 'permission_prompted',
    LEGACY_PERMISSION_ALLOWED = 'permission_accepted',
}

/** @internal */
export const SuppressedAsyncCommands = ['subscribe']

/** @public */
export type ECommProductRecord = {
    products: ECommItemWithQuantity[]
}

/** @public */
export type ECommProductRecordWithoutQuantity = {
    products: ECommItemWithoutQuantity[]
}

/** @public */
export type ECommEventRecord = {
    events: ECommItemWithQuantity[]
}

/** @public */
export type ECommEventRecordWithoutQuantity = {
    events: ECommItemWithoutQuantity[]
}

/** @public */
export type ECommProductRecordWithRating = {
    products: ECommItemWithRating[]
}

/** @public */
export type ECommEventRecordWithRating = {
    events: ECommItemWithRating[]
}

/** @public */
export type ECommPurchaseDetails = {
    price_value?: string
    purchase_id?: string
}

/** @public */
export type ECommPurchaseRecord =
    | (ECommProductRecord & ECommPurchaseDetails)
    | (ECommEventRecord & ECommPurchaseDetails)
    | (ECommProductRecordWithoutQuantity & ECommPurchaseDetails)
    | (ECommEventRecordWithoutQuantity & ECommPurchaseDetails)

/** @public */
export type LoadCommandValue = PushSdkConfig & { consentRequired?: boolean }
/** @public */
export type ShowPromptCommandValue = { id?: number; checkEligibility?: boolean }
/** @public */
export type ExternalIdCommandValue = string
/** @public */
export type PageTagVisitCommandValue = string | [string, ...string[]]
/** @public */
export type ProfileAppendCommandValue = { [key: string]: [string, ...string[]] }
/** @public */
export type ProfileRemoveCommandValue = { [key: string]: [string, ...string[]] }
/** @public */
export type ProfileCommandValue = { [key: string]: any }

/** @public */
export type PushArguments =
    // Generic Commands
    | [command: PushSdkCommand.LOAD, options: LoadCommandValue]
    | [command: PushSdkCommand.REQUEST_USER_DELETION]
    | [command: PushSdkCommand.OPT_OUT]
    | [command: PushSdkCommand.OPT_IN]
    | [command: PushSdkCommand.CONSENT, state: PushSdkConsentState]
    | [command: PushSdkCommand.DEBUG]
    | [command: PushSdkCommand.CONFIRM_CONSENT]
    // PushlySDK.UserProfile commands
    | [command: PushSdkCommand.EXTERNAL_ID, options: ExternalIdCommandValue]
    | [command: PushSdkCommand.DEREGISTER_EXTERNAL_ID]
    | [command: PushSdkCommand.PROFILE, options: ProfileCommandValue]
    | [command: PushSdkCommand.PROFILE_APPEND, options: ProfileAppendCommandValue]
    | [command: PushSdkCommand.PROFILE_REMOVE, options: ProfileRemoveCommandValue]
    | [command: PushSdkCommand.PAGE_TAG_VISIT, PageTagVisitCommandValue]
    // PushlySDK.PushNotifications commands
    | [command: PushSdkCommand.SHOW_PROMPT]
    | [command: PushSdkCommand.SHOW_PROMPT, options: ShowPromptCommandValue]
    | [command: PushSdkCommand.PAUSE_PUSH_NOTIFICATIONS]
    | [command: PushSdkCommand.RESUME_PUSH_NOTIFICATIONS]
    | [command: PushSdkCommand.GET_PUSH_NOTIFICATIONS_PAUSED_STATE, GetNotificationsPausedStateCallback]
    | [command: PushSdkCommand.SUPPRESS_PROMPTS]
    | [command: PushSdkCommand.ALLOW_PROMPTS]
    | [command: PushSdkCommand.GET_PROMPTING_SUPPRESSED_STATE, GetPromptingSuppressedStateCallback]
    // PushlySDK.EComm commands
    | [command: PushSdkCommand.VIEW_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity]
    | [command: PushSdkCommand.SAVE_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity]
    | [command: PushSdkCommand.UNSAVE_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity]
    | [command: PushSdkCommand.COMPLETE_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity]
    | [command: PushSdkCommand.UNCOMPLETE_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity]
    | [command: PushSdkCommand.RATE_ITEM, ECommProductRecordWithRating | ECommEventRecordWithRating]
    | [command: PushSdkCommand.UNRATE_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity]
    | [command: PushSdkCommand.ADD_TO_CART, ECommProductRecord | ECommEventRecord]
    | [command: PushSdkCommand.UPDATE_CART, ECommProductRecord | ECommEventRecord]
    | [command: PushSdkCommand.PURCHASE]
    | [command: PushSdkCommand.PURCHASE, ECommPurchaseRecord]
    | [command: PushSdkCommand.PURCHASE, ECommPurchaseDetails]
    // Events
    | [command: `on_${PushSdkEvent}`, PushSdkEventCallback]
